/**
 * BotaoGoogle.tsx — botão "Entrar com Google" (Google Identity Services).
 *
 * O GIS devolve um ID token (JWT) ao navegador; quem decide qualquer coisa é
 * o servidor, em POST /api/v1/auth/google, que verifica assinatura e `aud`
 * antes de olhar para o e-mail. Nada aqui autentica ninguém.
 *
 * O script vem de accounts.google.com e é carregado UMA vez, sob demanda —
 * só quando o ambiente tem Client ID configurado.
 */

import { useEffect, useRef } from 'react'

const SCRIPT_GIS = 'https://accounts.google.com/gsi/client'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (resposta: { credential: string }) => void
          }) => void
          renderButton: (elemento: HTMLElement, opcoes: Record<string, unknown>) => void
        }
      }
    }
  }
}

function carregarGis(): Promise<void> {
  const existente = document.querySelector<HTMLScriptElement>(
    `script[src="${SCRIPT_GIS}"]`,
  )
  if (existente) {
    return existente.dataset.pronto === 'sim'
      ? Promise.resolve()
      : new Promise((ok, falha) => {
          existente.addEventListener('load', () => ok())
          existente.addEventListener('error', () => falha(new Error('gis')))
        })
  }
  return new Promise((ok, falha) => {
    const script = document.createElement('script')
    script.src = SCRIPT_GIS
    script.async = true
    script.onload = () => {
      script.dataset.pronto = 'sim'
      ok()
    }
    script.onerror = () => falha(new Error('gis'))
    document.head.appendChild(script)
  })
}

export function BotaoGoogle({
  clientId,
  aoReceberCredencial,
  desabilitado = false,
}: {
  clientId: string
  aoReceberCredencial: (credential: string) => void
  desabilitado?: boolean
}) {
  const alvo = useRef<HTMLDivElement>(null)
  // O callback do GIS é registrado uma vez; a ref evita reinicializar o
  // widget a cada render só porque a função mudou de identidade.
  const callback = useRef(aoReceberCredencial)
  callback.current = aoReceberCredencial

  useEffect(() => {
    let cancelado = false
    void carregarGis()
      .then(() => {
        if (cancelado || !alvo.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resposta) => callback.current(resposta.credential),
        })
        window.google.accounts.id.renderButton(alvo.current, {
          theme: 'outline',
          // `medium` de propósito: com `large`, o GIS troca o texto do botão
          // pelo "botão personalizado" — nome e e-mail da conta Google ativa
          // no navegador, que continuava à mostra depois do logout. A
          // documentação lista tamanho `medium`/`small` (ou largura < 200px,
          // ou tipo `icon`) como as condições que suprimem a personalização.
          size: 'medium',
          width: 320,
          text: 'signin_with',
          shape: 'rectangular',
          locale: 'pt-BR',
        })
      })
      .catch(() => {
        /* sem rede ou script bloqueado: o login por senha continua ali */
      })
    return () => {
      cancelado = true
    }
  }, [clientId])

  return (
    <div
      ref={alvo}
      className={`flex justify-center ${desabilitado ? 'pointer-events-none opacity-50' : ''}`}
    />
  )
}
